import { Variants, Transition } from 'framer-motion';

/**
 * Modal Animation Configurations
 * Framer Motion variants and transitions for different modal animations
 */

// =============================================================================
// Basic Modal Animations
// =============================================================================

export const modalBackdropVariants: Variants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.15,
      ease: 'easeIn',
    },
  },
};

export const modalContentVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0.95,
    y: 20,
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.15,
      ease: 'easeIn',
    },
  },
};

// =============================================================================
// Specialized Modal Animations
// =============================================================================

export const slideUpVariants: Variants = {
  initial: {
    opacity: 0,
    y: '100%',
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    y: '100%',
    transition: {
      duration: 0.25,
      ease: 'easeIn',
    },
  },
};

export const slideDownVariants: Variants = {
  initial: {
    opacity: 0,
    y: '-100%',
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    y: '-100%',
    transition: {
      duration: 0.25,
      ease: 'easeIn',
    },
  },
};

export const slideLeftVariants: Variants = {
  initial: {
    opacity: 0,
    x: '100%',
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    x: '100%',
    transition: {
      duration: 0.25,
      ease: 'easeIn',
    },
  },
};

export const slideRightVariants: Variants = {
  initial: {
    opacity: 0,
    x: '-100%',
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    x: '-100%',
    transition: {
      duration: 0.25,
      ease: 'easeIn',
    },
  },
};

export const zoomVariants: Variants = {
  initial: {
    opacity: 0,
    scale: 0,
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.25,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    scale: 0,
    transition: {
      duration: 0.2,
      ease: 'easeIn',
    },
  },
};

export const flipVariants: Variants = {
  initial: {
    opacity: 0,
    rotateY: -90,
  },
  animate: {
    opacity: 1,
    rotateY: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    rotateY: 90,
    transition: {
      duration: 0.3,
      ease: 'easeIn',
    },
  },
};

// =============================================================================
// Staggered Animation Variants
// =============================================================================

export const staggerContainerVariants: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
  },
};

export const staggerItemVariants: Variants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.2,
      ease: 'easeIn',
    },
  },
};

// =============================================================================
// Animation Configuration Types
// =============================================================================

export type ModalAnimationType =
  | 'default'
  | 'slideUp'
  | 'slideDown'
  | 'slideLeft'
  | 'slideRight'
  | 'zoom'
  | 'flip'
  | 'stagger';

export interface ModalAnimationConfig {
  backdrop: Variants;
  content: Variants;
  duration?: number;
  easing?: Transition['ease'];
}

// =============================================================================
// Animation Getter Function
// =============================================================================

/**
 * Get animation variants based on animation type
 * @param type - Animation type
 * @returns Animation configuration
 */
export const getModalAnimations = (type: ModalAnimationType): ModalAnimationConfig => {
  switch (type) {
    case 'slideUp':
      return {
        backdrop: modalBackdropVariants,
        content: slideUpVariants,
      };
    case 'slideDown':
      return {
        backdrop: modalBackdropVariants,
        content: slideDownVariants,
      };
    case 'slideLeft':
      return {
        backdrop: modalBackdropVariants,
        content: slideLeftVariants,
      };
    case 'slideRight':
      return {
        backdrop: modalBackdropVariants,
        content: slideRightVariants,
      };
    case 'zoom':
      return {
        backdrop: modalBackdropVariants,
        content: zoomVariants,
      };
    case 'flip':
      return {
        backdrop: modalBackdropVariants,
        content: flipVariants,
      };
    case 'stagger':
      return {
        backdrop: modalBackdropVariants,
        content: staggerContainerVariants,
      };
    case 'default':
    default:
      return {
        backdrop: modalBackdropVariants,
        content: modalContentVariants,
      };
  }
};

// =============================================================================
// Custom Animation Builder
// =============================================================================

interface CustomAnimationOptions {
  duration?: number;
  easing?: Transition['ease'];
  initialScale?: number;
  initialY?: number;
  initialX?: number;
  initialRotation?: number;
}

/**
 * Create custom animation variants
 * @param options - Custom animation options
 * @returns Custom animation variants
 */
export const createCustomModalAnimations = (
  options: CustomAnimationOptions
): ModalAnimationConfig => {
  const {
    duration = 0.2,
    easing = 'easeOut',
    initialScale = 0.95,
    initialY = 20,
    initialX = 0,
    initialRotation = 0,
  } = options;

  const customVariants: Variants = {
    initial: {
      opacity: 0,
      scale: initialScale,
      y: initialY,
      x: initialX,
      rotate: initialRotation,
    },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
      x: 0,
      rotate: 0,
      transition: {
        duration,
        ease: easing,
      },
    },
    exit: {
      opacity: 0,
      scale: initialScale,
      y: initialY,
      x: initialX,
      rotate: initialRotation,
      transition: {
        duration: duration * 0.75,
        ease: 'easeIn',
      },
    },
  };

  return {
    backdrop: modalBackdropVariants,
    content: customVariants,
    duration,
    easing,
  };
};
